import React, { Component } from 'react'
import { connect } from 'react-redux'
import { isEmpty, isNil, head, find, propEq, propIs, pathOr } from 'ramda'

import { Loader, Div, Button, P, Span } from '@driftt/tide-core'
import SALESFORCE_ICON from 'images/sf-small.svg'
import { fetchSalesforceEnrichmentByEmail } from 'api/sfdcEnrichment'
import { fetchSalesforceEnrichmentStart, fetchSalesforceEnrichmentFailed, fetchSalesforceEnrichmentSuccess } from 'modules/sfdc/actions'

import './style.css'

class SalesforceSection extends Component {
  constructor(props) {
    super(props)

    this.state = {
      errorMessage: null
    }
  }

  componentDidMount(){
    this.enrich()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.email !== this.props.email) {
      this.enrich()
    }
  }

  enrich = async () => {
    const { email, fetchSalesforceEnrichmentStart, fetchSalesforceEnrichmentSuccess, fetchSalesforceEnrichmentFailed } = this.props

    fetchSalesforceEnrichmentStart(email)

    try {
      const { data } = await fetchSalesforceEnrichmentByEmail({ email })
      fetchSalesforceEnrichmentSuccess(this.toDisplayableSalesforceData(data), email)

      this.setState({
        errorMessage: null
      })
    } catch(e) {
      console.warn(e)

      const errorMessage = e.response.status === 404
        ? "Couldn't find matching record in Salesforce"
        : "Error fetching data from Salesforce"

      this.setState({ errorMessage })
      fetchSalesforceEnrichmentFailed(e.response, email)
    }
  }

  get isLoading() {
    const isLoading = pathOr(false, ['salesforceEnrichment', 'loading'], this.props)

    const noDataYet = isNil(this.props.salesforceEnrichment)

    return isLoading || noDataYet
  }

  get isError() {
    const error = pathOr({}, ['salesforceEnrichment', 'error'], this.props)

    return !isEmpty(error)
  }

  get salesforceData() {
    return pathOr(false, ['salesforceEnrichment', 'data'], this.props)
  }

  toDisplayableSalesforceData = data => {
    const account = data.accounts ? head(data.accounts) : null
    const contact = account ? find(propEq("AccountId", account.Id))(data.contacts) : null
    const lead = data.leads ? find(propIs(String, "Email"))(data.leads) : null
    const opportunity = data.opportunities ? find(propEq("IsClosed", false))(data.opportunities) : null
    return {
      account,
      contact,
      lead,
      opportunity,
      baseUrl: data.baseUrl,
    }
  }

  renderSalesforceInfo = () => {
    const data = this.salesforceData

    return (
      <Div>
        {!!data.opportunity && this.renderSalesforceOpportunityBar()}
        {this.renderRow("Account Name", pathOr("N/A", ['account', 'Name'], data))}
        {this.renderRow("Account Owner", pathOr(null, ['account', 'ownerName'], data))}
        {this.renderRow("Contact Name", pathOr(null, ['contact', 'Name'], data))}
        {this.renderRow("Contact Owner", pathOr(null, ['contact', 'ownerName'], data))}
        {this.renderRow("Lead Stage", pathOr(null, ['lead', 'Status'], data))}
        <Div className="sfdc-enrichment-body-row contacts-contact-sidebar--details--about-field">
          <Button type="subtle" href={pathOr(null, ['baseUrl'], data) + pathOr(pathOr(pathOr(null, ['account', 'Id'], data), ['lead', 'Id'], data), ['contact', 'Id'], data)} target="_blank" className="view-in-salesforce-link">
            View in Salesforce
          </Button>
        </Div>
      </Div>
    )
  }

  renderRow = (header, value) => {
    return (
      <Div className="sfdc-enrichment-body-row contacts-contact-sidebar--details--about-field">
        <Div className="sfdc-enrichment-row-header field-header">
          { header }
        </Div>
        <Div className="sfdc-enrichment-row-body field-value">
          { value ? value : "N/A" }
        </Div>
      </Div>
    )
  }

  renderSalesforceOpportunityBar = () => {
    return (
      <P className="salesforce-opportunity-bar">
        <span role="img" aria-label="cash">💰</span> This account has an open opportunity
      </P>
    )
  }

  renderErrorState = () => {
    const { errorMessage } = this.state

    if (!this.isError) {
      return null
    }

    return <P className="sfdc-error-message">{errorMessage}</P>
  }

  render() {
    const loading = this.isLoading
    const error = this.isError

    return(
      <Div>
        <Div className="sfdc-section-header">
          <img src={ SALESFORCE_ICON } className="header-icon" alt="salesforce"/>
          <Span className="sfdc-section-title">
            SALESFORCE DETAILS
          </Span>
        </Div>
        <Div className="sfdc-section-body">
          { loading && <Loader /> }
          { error && this.renderErrorState() }
          { (!loading && !error) && this.renderSalesforceInfo() }
        </Div>
      </Div>
    )
  }
}

const mapStateToProps = () => {
  return (state, props) => {
    const { email } = props

    return {
      salesforceEnrichment: state.__CRM.sfdcEnrichment.enrichment[email]
    }
  }
}

const mapDispatchToProps = dispatch => ({
  fetchSalesforceEnrichmentStart: email => {
    dispatch(fetchSalesforceEnrichmentStart(email))
  },
  fetchSalesforceEnrichmentFailed: (email, err) => {
    dispatch(fetchSalesforceEnrichmentFailed(email, err))
  },
  fetchSalesforceEnrichmentSuccess: (email, data) => {
    dispatch(fetchSalesforceEnrichmentSuccess(email, data))
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(SalesforceSection)
